import React, { useState } from "react";
import { toast } from "react-toastify";
import ExcelJS from "exceljs";
import * as XLSX from "xlsx";
import { useBulkUploadCampaignMutation } from "../../../../Services/campaign/CampaignMapping";

// Utility to get Excel column letters (A, B, ..., Z, AA, AB, ...)
function getExcelColumnLetter(colIndex: number): string {
  let letter = '';
  while (colIndex > 0) {
    let mod = (colIndex - 1) % 26;
    letter = String.fromCharCode(65 + mod) + letter;
    colIndex = Math.floor((colIndex - mod) / 26);
  }
  return letter;
}

interface IProps {
  filteredDynamicData: any;
  refetch: any;
  workflowSelected: any
}

const BulkLeadUploadCampaign: React.FC<IProps> = ({
  filteredDynamicData,
  refetch,
  workflowSelected
}) => {
  const [selectedCsvFiles, setSelectedCsvFiles] = useState<File[]>([]);
  const [fileToDownload, setFileToDownload] = useState<File | null>(null);
  const [failDetails, setFailDetails] = useState<any[]>([]); // Store fail details here
  const [triggerBulkUpload] = useBulkUploadCampaignMutation({});
  const [isAssigneeCheck, setIsAssigneeCheck] = useState(false);

  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const files = Array.from(event.target.files);
      setSelectedCsvFiles(files);
      setFileToDownload(files[0]);
    }
  };

  const handleRemoveCsvFile = (index: number) => {
    const newFiles = Array.from(selectedCsvFiles);
    newFiles.splice(index, 1);
    setSelectedCsvFiles(newFiles);
    if (index === 0 && newFiles.length > 0) {
      setFileToDownload(newFiles[0]); // Update the file to be downloaded if the first file was removed
    } else if (index === 0) {
      setFileToDownload(null); // Clear file to download if no files are left
    }
  };

  const handleCsvDownload = async () => {

    if (!workflowSelected) {
      toast.error("Please select a campaign first");
      return;
    }
    const workbook = new ExcelJS.Workbook();
    const ws: any = workbook.addWorksheet("Sheet 1");

    // Add hidden sheet for dropdown options
    const hiddenSheet = workbook.addWorksheet("DropdownOptions", {
      state: "hidden",
    });
    // Country codes array
    const countryCodes = [
      { code: '+93', iso: 'AF' },
      { code: '+355', iso: 'AL' },
      { code: '+213', iso: 'DZ' },
      { code: '+376', iso: 'AD' },
      { code: '+244', iso: 'AO' },
      { code: '+672', iso: 'AQ' },
      { code: '+54', iso: 'AR' },
      { code: '+374', iso: 'AM' },
      { code: '+297', iso: 'AW' },
      { code: '+61', iso: 'AU' },
      { code: '+43', iso: 'AT' },
      { code: '+994', iso: 'AZ' },
      { code: '+973', iso: 'BH' },
      { code: '+880', iso: 'BD' },
      { code: '+375', iso: 'BY' },
      { code: '+32', iso: 'BE' },
      { code: '+501', iso: 'BZ' },
      { code: '+229', iso: 'BJ' },
      { code: '+975', iso: 'BT' },
      { code: '+591', iso: 'BO' },
      { code: '+387', iso: 'BA' },
      { code: '+267', iso: 'BW' },
      { code: '+55', iso: 'BR' },
      { code: '+673', iso: 'BN' },
      { code: '+359', iso: 'BG' },
      { code: '+226', iso: 'BF' },
      { code: '+257', iso: 'BI' },
      { code: '+855', iso: 'KH' },
      { code: '+237', iso: 'CM' },
      { code: '+1', iso: 'CA' },
      { code: '+238', iso: 'CV' },
      { code: '+236', iso: 'CF' },
      { code: '+235', iso: 'TD' },
      { code: '+56', iso: 'CL' },
      { code: '+86', iso: 'CN' },
      { code: '+57', iso: 'CO' },
      { code: '+269', iso: 'KM' },
      { code: '+682', iso: 'CK' },
      { code: '+506', iso: 'CR' },
      { code: '+385', iso: 'HR' },
      { code: '+53', iso: 'CU' },
      { code: '+599', iso: 'CW' },
      { code: '+357', iso: 'CY' },
      { code: '+420', iso: 'CZ' },
      { code: '+243', iso: 'CD' },
      { code: '+45', iso: 'DK' },
      { code: '+253', iso: 'DJ' },
      { code: '+670', iso: 'TL' },
      { code: '+593', iso: 'EC' },
      { code: '+20', iso: 'EG' },
      { code: '+503', iso: 'SV' },
      { code: '+240', iso: 'GQ' },
      { code: '+291', iso: 'ER' },
      { code: '+372', iso: 'EE' },
      { code: '+251', iso: 'ET' },
      { code: '+500', iso: 'FK' },
      { code: '+298', iso: 'FO' },
      { code: '+679', iso: 'FJ' },
      { code: '+358', iso: 'FI' },
      { code: '+33', iso: 'FR' },
      { code: '+689', iso: 'PF' },
      { code: '+241', iso: 'GA' },
      { code: '+220', iso: 'GM' },
      { code: '+995', iso: 'GE' },
      { code: '+49', iso: 'DE' },
      { code: '+233', iso: 'GH' },
      { code: '+350', iso: 'GI' },
      { code: '+30', iso: 'GR' },
      { code: '+299', iso: 'GL' },
      { code: '+502', iso: 'GT' },
      { code: '+224', iso: 'GN' },
      { code: '+245', iso: 'GW' },
      { code: '+592', iso: 'GY' },
      { code: '+509', iso: 'HT' },
      { code: '+504', iso: 'HN' },
      { code: '+852', iso: 'HK' },
      { code: '+36', iso: 'HU' },
      { code: '+354', iso: 'IS' },
      { code: '+91', iso: 'IN' },
      { code: '+62', iso: 'ID' },
      { code: '+98', iso: 'IR' },
      { code: '+964', iso: 'IQ' },
      { code: '+353', iso: 'IE' },
      { code: '+972', iso: 'IL' },
      { code: '+39', iso: 'IT' },
      { code: '+225', iso: 'CI' },
      { code: '+81', iso: 'JP' },
      { code: '+962', iso: 'JO' },
      { code: '+7', iso: 'KZ' },
      { code: '+254', iso: 'KE' },
      { code: '+686', iso: 'KI' },
      { code: '+383', iso: 'XK' },
      { code: '+965', iso: 'KW' },
      { code: '+996', iso: 'KG' },
      { code: '+856', iso: 'LA' },
      { code: '+371', iso: 'LV' },
      { code: '+961', iso: 'LB' },
      { code: '+266', iso: 'LS' },
      { code: '+231', iso: 'LR' },
      { code: '+218', iso: 'LY' },
      { code: '+423', iso: 'LI' },
      { code: '+370', iso: 'LT' },
      { code: '+352', iso: 'LU' },
      { code: '+853', iso: 'MO' },
      { code: '+389', iso: 'MK' },
      { code: '+261', iso: 'MG' },
      { code: '+265', iso: 'MW' },
      { code: '+60', iso: 'MY' },
      { code: '+960', iso: 'MV' },
      { code: '+223', iso: 'ML' },
      { code: '+356', iso: 'MT' },
      { code: '+692', iso: 'MH' },
      { code: '+222', iso: 'MR' },
      { code: '+230', iso: 'MU' },
      { code: '+52', iso: 'MX' },
      { code: '+691', iso: 'FM' },
      { code: '+373', iso: 'MD' },
      { code: '+377', iso: 'MC' },
      { code: '+976', iso: 'MN' },
      { code: '+382', iso: 'ME' },
      { code: '+212', iso: 'MA' },
      { code: '+258', iso: 'MZ' },
      { code: '+95', iso: 'MM' },
      { code: '+264', iso: 'NA' },
      { code: '+674', iso: 'NR' },
      { code: '+977', iso: 'NP' },
      { code: '+31', iso: 'NL' },
      { code: '+64', iso: 'NZ' },
      { code: '+234', iso: 'NG' },
      { code: '+47', iso: 'NO' },
      { code: '+968', iso: 'OM' },
      { code: '+92', iso: 'PK' },
      { code: '+63', iso: 'PH' },
      { code: '+48', iso: 'PL' },
      { code: '+351', iso: 'PT' },
      { code: '+974', iso: 'QA' },
      { code: '+40', iso: 'RO' },
      { code: '+7', iso: 'RU' },
      { code: '+966', iso: 'SA' },
      { code: '+65', iso: 'SG' },
      { code: '+27', iso: 'ZA' },
      { code: '+34', iso: 'ES' },
      { code: '+94', iso: 'LK' },
      { code: '+46', iso: 'SE' },
      { code: '+41', iso: 'CH' },
      { code: '+90', iso: 'TR' },
      { code: '+971', iso: 'AE' },
      { code: '+44', iso: 'GB' },
      { code: '+1', iso: 'US' },
      { code: '+84', iso: 'VN' },
      { code: '+967', iso: 'YE' },
      { code: '+263', iso: 'ZW' }
    ];
    // Populate hidden sheet with country codes
    countryCodes.forEach((country, index) => {
      hiddenSheet.getCell(`A${index + 1}`).value = `${country.iso} (${country.code})`;
    });

    let header = [
      "campaign_account_name",
      "campaign_first_name",
      "campaign_last_name",
      "campaign_email",
      "campaign_country_code",
      "campaign_phone_number",
      ...(isAssigneeCheck ? ["campaign_assignee"] : []) // Add dynamically if isAssigneeCheck is true
    ];

    // Check if dynamicData and fields exist
    // Dynamically add dynamic field headers
    if (filteredDynamicData && filteredDynamicData.length) {
      filteredDynamicData.forEach((field) => {
        header.push(`campaign_${field.label}`); // Dynamic field label in header
      });
    }

    // Add header row to the worksheet
    ws.addRow(header);

    // Set column widths
    ws.columns.map((col) => (col.width = 18));

    // Set phone number column to text format to prevent scientific notation
    const phoneIndex = header.indexOf("campaign_phone_number");
    if (phoneIndex !== -1) {
      const phoneColumn = ws.getColumn(phoneIndex + 1);
      phoneColumn.numFmt = "@"; // Text format
    }

    // Add data validation for country code column
    const countryCodeIndex = header.indexOf("campaign_country_code");
    if (countryCodeIndex !== -1) {
      const columnLetter = getExcelColumnLetter(countryCodeIndex + 1);

      // Style the header
      ws.getCell(1, countryCodeIndex + 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF0000" }, // Red background
      };
      // Add dropdown validation for country code column
      for (let r = 2; r <= 100; r++) {
        ws.getCell(`${columnLetter}${r}`).dataValidation = {
          type: "list",
          allowBlank: false,
          showErrorMessage: true,
          errorTitle: "Invalid Country Code",
          error: "Please select a valid country code",
          formulae: [`DropdownOptions!$A$1:$A$${countryCodes.length}`],
        };
      }
    }

    filteredDynamicData.forEach((field, index) => {
      if (field.field_type === "check-box") {
        const checkboxOptions = field.multiple_choices
          .map((choice) => choice.choice)
          .join(",");

        // Determine the column index for this field in the header
        const columnIndex =
          header.length - filteredDynamicData.length + index + 1; // Adjust for the current dynamic field
        const columnLetter = String.fromCharCode(64 + columnIndex);

        // Add dropdown validation for this field's column
        for (let i = 2; i <= 100; i++) {
          // Apply validation to rows 2-100
          ws.getCell(`${columnLetter}${i}`).dataValidation = {
            type: "list",
            allowBlank: false,
            formulae: [`"${checkboxOptions}"`], // Using all options without truncation
          };
        }
      } else if (field.field_type === "drop-down") {
        const fieldOptions = field?.choices.map((option: any) => option.choice);

        // Determine the column index for this field in the header
        const columnIndex =
          header.length - filteredDynamicData.length + index + 1; // Adjust for the current dynamic field
        const columnLetter = String.fromCharCode(64 + columnIndex);

        // Add dropdown validation for this field's column
        for (let i = 2; i <= 100; i++) {
          // Apply validation to rows 2-100
          ws.getCell(`${columnLetter}${i}`).dataValidation = {
            type: "list",
            allowBlank: false,
            formulae: [`"${fieldOptions}"`], // Using all options without truncation
          };
        }
      }
    });

    // Apply specific red fill style to headers
    const redHeaders = [
      "campaign_country_code",
      "campaign_phone_number",
    ];
    redHeaders.forEach((headerName) => {
      const colIndex = header.indexOf(headerName) + 1; // Find column index for each red header
      ws.getCell(1, colIndex).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF0000" }, // Red color for specific headers
      };
    });

    // Apply general styles to all cells
    ws.eachRow((row) => {
      row.eachCell((cell) => {
        cell.font = {
          name: "Inter",
          size: 8,
        };
        cell.alignment = {
          horizontal: "center",
        };
      });
    });

    // Check if dynamicData and fields exist
    if (filteredDynamicData && filteredDynamicData.length) {
      filteredDynamicData.forEach((field, index) => {
        if (field.is_required) {
          const columnIndex =
            header.length - filteredDynamicData.length + index + 1; // Adjust for the current dynamic field
          const columnLetter = String.fromCharCode(64 + columnIndex);
          ws.getCell(`${columnLetter}1`).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFF0000" }, // Red color
          };
        }
      });
    }

    // Generate the Excel file as a blob
    const excelBlob = await workbook.xlsx.writeBuffer();
    const excelUrl = URL.createObjectURL(
      new Blob([excelBlob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
    );

    // Create a link to download the Excel file
    const link = document.createElement("a");
    link.href = excelUrl;
    link.download = "campaign_template.xlsx";
    document.body.appendChild(link);
    link.click();

    // Clean up
    URL.revokeObjectURL(excelUrl);
    document.body.removeChild(link);
  };


  const handleDownloadFailDetails = async () => {
    const workbook = new ExcelJS.Workbook();
    const ws = workbook.addWorksheet("Failed Uploads");

    // Prepare header for static fields
    let header = [
      "campaign_account_name",
      "campaign_first_name",
      "campaign_last_name",
      "campaign_email",
      "campaign_country_code",
      "campaign_phone_number",
    ];

    // Dynamically add dynamic field headers
    if (filteredDynamicData && filteredDynamicData.length) {
      filteredDynamicData.forEach((field) => {
        const csvKey = field.label.replace(/ /g, "_"); // Replace spaces with underscores
        header.push(`campaign_${csvKey}`); // Dynamic field label in header
      });
    }

    // Errors column is added at the last
    header.push("Errors");

    const hiddenSheet = workbook.addWorksheet("DropdownOptions", {
      state: "hidden",
    });

    const countryCodes = [
      { code: '+93', iso: 'AF' },
      { code: '+355', iso: 'AL' },
      { code: '+213', iso: 'DZ' },
      { code: '+376', iso: 'AD' },
      { code: '+244', iso: 'AO' },
      { code: '+672', iso: 'AQ' },
      { code: '+54', iso: 'AR' },
      { code: '+374', iso: 'AM' },
      { code: '+297', iso: 'AW' },
      { code: '+61', iso: 'AU' },
      { code: '+43', iso: 'AT' },
      { code: '+994', iso: 'AZ' },
      { code: '+973', iso: 'BH' },
      { code: '+880', iso: 'BD' },
      { code: '+375', iso: 'BY' },
      { code: '+32', iso: 'BE' },
      { code: '+501', iso: 'BZ' },
      { code: '+229', iso: 'BJ' },
      { code: '+975', iso: 'BT' },
      { code: '+591', iso: 'BO' },
      { code: '+387', iso: 'BA' },
      { code: '+267', iso: 'BW' },
      { code: '+55', iso: 'BR' },
      { code: '+673', iso: 'BN' },
      { code: '+359', iso: 'BG' },
      { code: '+226', iso: 'BF' },
      { code: '+257', iso: 'BI' },
      { code: '+855', iso: 'KH' },
      { code: '+237', iso: 'CM' },
      { code: '+1', iso: 'CA' },
      { code: '+238', iso: 'CV' },
      { code: '+236', iso: 'CF' },
      { code: '+235', iso: 'TD' },
      { code: '+56', iso: 'CL' },
      { code: '+86', iso: 'CN' },
      { code: '+57', iso: 'CO' },
      { code: '+269', iso: 'KM' },
      { code: '+682', iso: 'CK' },
      { code: '+506', iso: 'CR' },
      { code: '+385', iso: 'HR' },
      { code: '+53', iso: 'CU' },
      { code: '+599', iso: 'CW' },
      { code: '+357', iso: 'CY' },
      { code: '+420', iso: 'CZ' },
      { code: '+243', iso: 'CD' },
      { code: '+45', iso: 'DK' },
      { code: '+253', iso: 'DJ' },
      { code: '+670', iso: 'TL' },
      { code: '+593', iso: 'EC' },
      { code: '+20', iso: 'EG' },
      { code: '+503', iso: 'SV' },
      { code: '+240', iso: 'GQ' },
      { code: '+291', iso: 'ER' },
      { code: '+372', iso: 'EE' },
      { code: '+251', iso: 'ET' },
      { code: '+500', iso: 'FK' },
      { code: '+298', iso: 'FO' },
      { code: '+679', iso: 'FJ' },
      { code: '+358', iso: 'FI' },
      { code: '+33', iso: 'FR' },
      { code: '+689', iso: 'PF' },
      { code: '+241', iso: 'GA' },
      { code: '+220', iso: 'GM' },
      { code: '+995', iso: 'GE' },
      { code: '+49', iso: 'DE' },
      { code: '+233', iso: 'GH' },
      { code: '+350', iso: 'GI' },
      { code: '+30', iso: 'GR' },
      { code: '+299', iso: 'GL' },
      { code: '+502', iso: 'GT' },
      { code: '+224', iso: 'GN' },
      { code: '+245', iso: 'GW' },
      { code: '+592', iso: 'GY' },
      { code: '+509', iso: 'HT' },
      { code: '+504', iso: 'HN' },
      { code: '+852', iso: 'HK' },
      { code: '+36', iso: 'HU' },
      { code: '+354', iso: 'IS' },
      { code: '+91', iso: 'IN' },
      { code: '+62', iso: 'ID' },
      { code: '+98', iso: 'IR' },
      { code: '+964', iso: 'IQ' },
      { code: '+353', iso: 'IE' },
      { code: '+972', iso: 'IL' },
      { code: '+39', iso: 'IT' },
      { code: '+225', iso: 'CI' },
      { code: '+81', iso: 'JP' },
      { code: '+962', iso: 'JO' },
      { code: '+7', iso: 'KZ' },
      { code: '+254', iso: 'KE' },
      { code: '+686', iso: 'KI' },
      { code: '+383', iso: 'XK' },
      { code: '+965', iso: 'KW' },
      { code: '+996', iso: 'KG' },
      { code: '+856', iso: 'LA' },
      { code: '+371', iso: 'LV' },
      { code: '+961', iso: 'LB' },
      { code: '+266', iso: 'LS' },
      { code: '+231', iso: 'LR' },
      { code: '+218', iso: 'LY' },
      { code: '+423', iso: 'LI' },
      { code: '+370', iso: 'LT' },
      { code: '+352', iso: 'LU' },
      { code: '+853', iso: 'MO' },
      { code: '+389', iso: 'MK' },
      { code: '+261', iso: 'MG' },
      { code: '+265', iso: 'MW' },
      { code: '+60', iso: 'MY' },
      { code: '+960', iso: 'MV' },
      { code: '+223', iso: 'ML' },
      { code: '+356', iso: 'MT' },
      { code: '+692', iso: 'MH' },
      { code: '+222', iso: 'MR' },
      { code: '+230', iso: 'MU' },
      { code: '+52', iso: 'MX' },
      { code: '+691', iso: 'FM' },
      { code: '+373', iso: 'MD' },
      { code: '+377', iso: 'MC' },
      { code: '+976', iso: 'MN' },
      { code: '+382', iso: 'ME' },
      { code: '+212', iso: 'MA' },
      { code: '+258', iso: 'MZ' },
      { code: '+95', iso: 'MM' },
      { code: '+264', iso: 'NA' },
      { code: '+674', iso: 'NR' },
      { code: '+977', iso: 'NP' },
      { code: '+31', iso: 'NL' },
      { code: '+64', iso: 'NZ' },
      { code: '+234', iso: 'NG' },
      { code: '+47', iso: 'NO' },
      { code: '+968', iso: 'OM' },
      { code: '+92', iso: 'PK' },
      { code: '+63', iso: 'PH' },
      { code: '+48', iso: 'PL' },
      { code: '+351', iso: 'PT' },
      { code: '+974', iso: 'QA' },
      { code: '+40', iso: 'RO' },
      { code: '+7', iso: 'RU' },
      { code: '+966', iso: 'SA' },
      { code: '+65', iso: 'SG' },
      { code: '+27', iso: 'ZA' },
      { code: '+34', iso: 'ES' },
      { code: '+94', iso: 'LK' },
      { code: '+46', iso: 'SE' },
      { code: '+41', iso: 'CH' },
      { code: '+90', iso: 'TR' },
      { code: '+971', iso: 'AE' },
      { code: '+44', iso: 'GB' },
      { code: '+1', iso: 'US' },
      { code: '+84', iso: 'VN' },
      { code: '+967', iso: 'YE' },
      { code: '+263', iso: 'ZW' }
    ];


    countryCodes.forEach((country, index) => {
      hiddenSheet.getCell(`A${index + 1}`).value = `${country.iso} (${country.code})`;
    });

    // Add headers to the worksheet
    ws.addRow(header);

    // Set column widths
    ws.columns.map((col) => (col.width = 18));

    // Set phone number column to text format to prevent scientific notation
    const phoneIndex = header.indexOf("campaign_phone_number");
    if (phoneIndex !== -1) {
      const phoneColumn = ws.getColumn(phoneIndex + 1);
      phoneColumn.numFmt = "@"; // Text format
    }

    // Iterate through failDetails to populate rows
    failDetails.forEach((fail) => {
      const {
        account_name,
        first_name,
        last_name,
        email,
        phone_number,
        dynamic_fields = {},
        error,
      } = fail;

      const countryCode = dynamic_fields.country_code;
      // Prepare dynamic fields data in the same structure as CSV
      const dynamicFieldValues = filteredDynamicData.map((field) => {
        const fieldKey = field.label.replace(/ /g, "_");
        return dynamic_fields[fieldKey] || ""; // Return 'N/A' if value is missing
      });

      // Convert errors into string for easier display in Excel
      const errorString = Array.isArray(error)
        ? error.join(", ")
        : JSON.stringify(error);

      ws.addRow([
        account_name,
        first_name,
        last_name,
        email,
        countryCode,
        phone_number,

        ...dynamicFieldValues, // Spread dynamic field values here
        errorString, // Errors at the end
      ]);
    });

      // Apply styles to all cells
      ws.eachRow((row) => {
        row.eachCell((cell) => {
          cell.font = {
            name: "Inter",
            size: 8,
          };
          cell.alignment = {
            horizontal: "center",
          };
        });
      });

    // Apply specific red fill style to headers
    const redHeaders = [
      "campaign_phone_number",
      "campaign_country_code",
    ];
    redHeaders.forEach((headerName) => {
      const colIndex = header.indexOf(headerName) + 1; // Find column index for each red header
      ws.getCell(1, colIndex).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF0000" }, // Red color for specific headers
      };
    });

    // Apply red fill to required dynamic field headers
    if (filteredDynamicData && filteredDynamicData.length) {
      filteredDynamicData.forEach((field, index) => {
        if (field.is_required) {
          const columnIndex =
            header.indexOf(`campaign_${field.label.replace(/ /g, "_")}`) + 1;
            ws.getCell(1, columnIndex).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFFF0000" }, // Red color for required dynamic fields
          };
        }
      });
    }
    // Add data validation for country code column
    const countryCodeIndex = header.indexOf("campaign_country_code");
    if (countryCodeIndex !== -1) {
      const columnLetter = getExcelColumnLetter(countryCodeIndex + 1);

      // Style the header
      ws.getCell(1, countryCodeIndex + 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFF0000" }, // Red background
      };
      // Add dropdown validation for country code column
      for (let r = 2; r <= 100; r++) {
        ws.getCell(`${columnLetter}${r}`).dataValidation = {
          type: "list",
          allowBlank: false,
          showErrorMessage: true,
          errorTitle: "Invalid Country Code",
          error: "Please select a valid country code",
          formulae: [`DropdownOptions!$A$1:$A$${countryCodes.length}`],
        };
      }
    }

    // Create the Excel file and initiate the download
    const excelBlob = await workbook.xlsx.writeBuffer();
    const excelUrl = URL.createObjectURL(
      new Blob([excelBlob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
    );

    const link = document.createElement("a");
    link.href = excelUrl;
    link.download = "failed_uploads.xlsx";
    document.body.appendChild(link);
    link.click();

    // Clean up temporary URLs and DOM elements
    URL.revokeObjectURL(excelUrl);
    document.body.removeChild(link);
  };
 
 const readFileAsText = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject("File reading failed");
      }
    };
    reader.onerror = () => reject("File reading error");
    reader.readAsText(file);
  });
};

const readFileAsBinaryString = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        resolve(reader.result);
      } else {
        reject("File reading failed");
      }
    };
    reader.onerror = () => reject("File reading error");
    reader.readAsBinaryString(file);
  });
};

  const toBase64UTF8 = (str: string) =>
    btoa(
      encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, (_, p1) =>
        String.fromCharCode(parseInt(p1, 16))
      )
    );
const handleFormSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
  event.preventDefault();

  if (!workflowSelected) {
    toast.error("Please select a campaign first");
    return;
  }

  if (selectedCsvFiles.length === 0) {
    toast.error("Please select a CSV or XLSX file to upload");
    return;
  }

  const file = selectedCsvFiles[0];
  const fileExtension = file.name.split(".").pop()?.toLowerCase();
  let csvContent: string | undefined;

  try {
    if (fileExtension === "csv") {
      const text = await readFileAsText(file);
      csvContent = text;
    } else if (fileExtension === "xlsx") {
      const binaryStr = await readFileAsBinaryString(file);
      const workbook = XLSX.read(binaryStr, { type: "binary" });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      csvContent = XLSX.utils.sheet_to_csv(worksheet);
    } else {
      toast.error("Unsupported file format. Please upload a CSV or XLSX file.");
      return;
    }

    if (csvContent) {
      const base64Csv = toBase64UTF8(csvContent);
      const payload = {
        campaign: workflowSelected,
        file_name: file.name,
        csv_file: `data:text/csv;base64,${base64Csv}`,
      };

      const response = await triggerBulkUpload({ body: payload });

      if (response) {
        const successCount = response.data.success_count;
        const failCount = response.data.fail_count;

        setFailDetails(response.data.fail_details);

        if (successCount) {
          toast.success(`${successCount} record(s) uploaded successfully`);
        }
        if (failCount > 0) {
          toast.error(`${failCount} record(s) failed to upload`);
        }

        setSelectedCsvFiles([]);
        setFileToDownload(null);
        refetch();
      }
    }
  } catch (error) {
    toast.error("Upload failed. Please try again.");
    console.error("File upload error:", error);
  }
};

  const checkboxStyle: any = {
    width: "20px",
    height: "20px",
    border: "1px solid #6366f1",
    backgroundColor: isAssigneeCheck ? "#6366f1" : "transparent",
    position: "relative",
    cursor: "pointer",
    display: "inline-block",
    appearance: "none", // Removes default styling
    WebkitAppearance: "none", // For WebKit browsers
  };

  const checkmarkStyle: any = {
    content: '""',
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%) rotate(45deg)",
    width: "5px",
    height: "10px",
    border: "solid white",
    borderWidth: "0 2px 2px 0",
    display: isAssigneeCheck ? "block" : "none", // Only show when checked
  };

  return (
    <form onSubmit={handleFormSubmit}>
      <div className="mb-4">
        <div className="d-flex justify-content-start mt-2 mb-8 mx-1">
          {/* CSV Upload Button */}
          <label
            htmlFor="upload-csv-button"
            className="form-control d-flex align-items-start justify-content-center bg-secondary fw-bold mx-2 px-3 py-2 rounded mw-200px"
            style={{ cursor: "pointer" }}
          >
            <i className="bi bi-file-earmark-spreadsheet text-dark fs-4 me-2 mt-1"></i>
            CSV/XLSX Upload
          </label>
          <input
            type="file"
            id="upload-csv-button"
            accept=".csv, .xlsx"
            style={{ display: "none" }}
            onChange={handleCsvFileChange}
          />

          {/* CSV Download Button */}
          <button
            type="button"
            className="form-control d-flex align-items-center justify-content-center bg-secondary fw-bold mx-2 px-3 py-2 rounded mw-150px"
            onClick={handleCsvDownload}
            style={{ cursor: "pointer" }}
          >
            <i className="bi bi-download text-dark fs-4 me-2"></i>
            Download File
          </button>
        </div>
        <div className="d-flex justify-content-start mt-2 mb-8 mx-4">
          <div
            style={checkboxStyle}
            onClick={() => setIsAssigneeCheck(!isAssigneeCheck)} // Handle toggle
          >
            <div style={checkmarkStyle}></div>
          </div>
          <label htmlFor="show-fail-details" className="ms-2">Assign to user</label>
        </div>

        <div className="mt-3 mb-3">
          <label className="fs-6 fw-bolder mb-2">Important Instructions:</label>
          <br />
          <label className="text-danger">
            &bull; Please upload a valid CSV or XLSX file format only.
          </label>
          <br />
          <label className="text-danger">
            &bull; Headers highlighted in red are mandatory fields.
          </label>
        </div>


        {failDetails?.length > 0 && (
          <button
            type="button"
            className="form-control d-flex align-items-center justify-content-center bg-secondary fw-bold mx-6 px-3 py-2 rounded mw-150px"
            onClick={handleDownloadFailDetails}
            style={{ cursor: "pointer" }}
          >
            <i className="bi bi-download text-dark fs-4 me-2"></i>
            Error Data
          </button>
        )}
        {/* Display selected CSV files */}
        {selectedCsvFiles?.length > 0 && (
          <div className="mb-4">
            <h5>Selected CSV files:</h5>
            <ul className="list-group mt-3">
              {selectedCsvFiles.map((file, index) => (
                <li
                  key={index}
                  className="list-group-item d-flex justify-content-between align-items-center"
                >
                  <span>{file.name}</span>
                  <button
                    type="button"
                    className="btn btn-link text-danger p-0"
                    onClick={() => handleRemoveCsvFile(index)}
                  >
                    <i className="bi bi-trash text-danger fs-5"></i>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <div className="d-flex justify-content-end mb-4 pt-2">
        <button type="submit" className="btn btn-sm btn-primary">
          Submit
        </button>
      </div>
    </form>
  );
};

export default BulkLeadUploadCampaign;